/**
 * MidfielderBot - Hard mode midfielder AI.
 * Moves in central band (z: -10 to 10), links defense and attack.
 * Supports defender, receives passes, advances ball, passes to striker.
 */

import * as BABYLON from "babylonjs";
import { Ball } from "./Ball";
import { Player } from "./Player";
import { BotBase } from "./BotBase";
import { AIRole, AIState, AllowedZone, SeparationConfig } from "../systems/DifficultyManager";
import { KickType } from "./HumanoidMesh";

// Midfielder-specific constants
const PASS_POWER = 10;
const PASS_COOLDOWN = 1.0;
const SUPPORT_DISTANCE = 8;
const ADVANCE_SPEED = 6;

/**
 * Hard mode midfielder bot
 * - Zone restriction: central band (z: -10 to 10)
 * - States: SUPPORT, RECEIVE, PASS, ADVANCE, SHOOT
 * - Behavior: Link between defense and attack, find space, pass to striker
 */
export class MidfielderBot extends BotBase {
  private _passTimer = 0;
  private _defenderPosition: BABYLON.Vector3 | null = null;
  private _strikerPosition: BABYLON.Vector3 | null = null;

  constructor(
    scene: BABYLON.Scene,
    ball: Ball,
    homePosition: BABYLON.Vector3,
    allowedZone: AllowedZone,
    separationConfig: SeparationConfig,
    shadowGenerator?: BABYLON.ShadowGenerator,
    player?: Player
  ) {
    super(
      scene,
      ball,
      AIRole.MIDFIELDER,
      homePosition,
      allowedZone,
      separationConfig,
      shadowGenerator,
      player,
      "midfielderBot"
    );
  }

  /**
   * Green jersey color for midfielder
   */
  protected _getJerseyColor(): BABYLON.Color3 {
    return new BABYLON.Color3(0.1, 0.7, 0.2); // Green jersey
  }

  /**
   * Set defender position reference for receiving passes
   */
  setDefenderPosition(pos: BABYLON.Vector3): void {
    this._defenderPosition = pos;
  }

  /**
   * Set striker position reference for passing
   */
  setStrikerPosition(pos: BABYLON.Vector3): void {
    this._strikerPosition = pos;
  }

  /**
   * Update with pass timer
   */
  update(dt: number, teammates: BotBase[]): void {
    if (this._passTimer > 0) this._passTimer -= dt;
    super.update(dt, teammates);
  }

  /**
   * Midfielder-specific decision making
   */
  protected _makeDecision(
    distToBall: number,
    distToGoal: number,
    ballPos: BABYLON.Vector3,
    myPos: BABYLON.Vector3
  ): void {
    // Check if ball is in our zone (central band)
    const ballInOurZone = ballPos.z >= this._allowedZone.minZ && ballPos.z <= this._allowedZone.maxZ;

    // If we have possession and are in shooting range
    if (this.hasBallPossession && distToGoal < 18 && Math.abs(myPos.x) < 5) {
      this._state = AIState.SHOOT;
      return;
    }

    // If we have possession, look to advance or pass
    if (this.hasBallPossession && this._passTimer <= 0) {
      // Check if striker is available for pass
      if (this._strikerPosition && this._strikerPosition.z < myPos.z - 5) {
        this._state = AIState.PASS;
      } else {
        // No striker available, advance with ball
        this._state = AIState.ADVANCE;
      }
      return;
    }

    // If we have possession but can't pass yet, dribble
    if (this.hasBallPossession) {
      this._state = AIState.DRIBBLE;
      return;
    }

    // Tackle if player has ball and we're close
    if (this._player && this._player.hasBallPossession && distToBall < 2.5 && this._tackleCooldown <= 0) {
      this._state = AIState.TACKLE;
      return;
    }

    // If ball is in our zone, chase it
    if (ballInOurZone) {
      this._state = AIState.CHASE_BALL;
      return;
    }

    // If ball is in defender's zone, provide support
    if (ballPos.z > this._allowedZone.maxZ) {
      this._state = AIState.SUPPORT;
      return;
    }

    // If ball is in striker's zone, move to receive potential clearance
    if (ballPos.z < this._allowedZone.minZ) {
      this._state = AIState.RECEIVE;
      return;
    }

    // Default: return to home
    this._state = AIState.RETURN_HOME;
  }

  /**
   * Handle state behavior with midfielder-specific states
   */
  protected _handleState(
    ballPos: BABYLON.Vector3,
    myPos: BABYLON.Vector3,
    distToBall: number,
    distToGoal: number,
    dt: number
  ): void {
    switch (this._state) {
      case AIState.SUPPORT:
        this._handleSupport(ballPos, myPos, dt);
        break;
      case AIState.RECEIVE:
        this._handleReceive(ballPos, myPos, distToBall, dt);
        break;
      case AIState.PASS:
        this._handlePass(ballPos, myPos);
        break;
      case AIState.ADVANCE:
        this._handleAdvance(ballPos, myPos, distToGoal, dt);
        break;
      default:
        super._handleState(ballPos, myPos, distToBall, distToGoal, dt);
    }
  }

  /**
   * Support play - position for pass options when defender has ball
   */
  private _handleSupport(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, dt: number): void {
    // Position between defender and striker, ready to receive
    let targetX = ballPos.x * 0.5; // Offset from ball
    let targetZ = this._allowedZone.maxZ - 3; // Near top of our zone

    // Create space by moving away from ball horizontally
    if (Math.abs(ballPos.x - myPos.x) < 3) {
      targetX = myPos.x + (myPos.x > 0 ? 5 : -5);
    }

    // Clamp to zone
    targetX = BABYLON.Scalar.Clamp(targetX, this._allowedZone.minX, this._allowedZone.maxX);
    targetZ = BABYLON.Scalar.Clamp(targetZ, this._allowedZone.minZ, this._allowedZone.maxZ);

    const targetPos = new BABYLON.Vector3(targetX, 0, targetZ);
    const dx = targetPos.x - myPos.x;
    const dz = targetPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist > 0.5) {
      const speed = 5.5 * 0.7;
      const vx = (dx / dist) * speed;
      const vz = (dz / dist) * speed;
      myPos.x += vx * dt;
      myPos.z += vz * dt;
      this._currentSpeed = speed;
      this._velocity.set(vx, 0, vz);

      const angle = Math.atan2(dx, dz);
      this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.1);
    } else {
      this._currentSpeed = 0;
      // Face the ball
      const toBall = ballPos.subtract(myPos);
      const angle = Math.atan2(toBall.x, toBall.z);
      this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.1);
    }
  }

  /**
   * Receive ball behavior - move to intercept incoming ball
   */
  private _handleReceive(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, distToBall: number, dt: number): void {
    // Predict ball trajectory and move to intercept
    const ballVel = this._ball.mesh.physicsImpostor?.getLinearVelocity();
    let targetPos: BABYLON.Vector3;

    if (ballVel && ballVel.length() > 2) {
      // Ball is moving - predict where to intercept
      const ballDir = new BABYLON.Vector3(ballVel.x, 0, ballVel.z).normalize();
      const timeToIntercept = distToBall / Math.max(ballVel.length(), 1);
      targetPos = ballPos.add(ballDir.scale(timeToIntercept * 2));
    } else {
      // Ball is slow - position near bottom of zone
      targetPos = new BABYLON.Vector3(ballPos.x * 0.3, 0, this._allowedZone.minZ + 3);
    }

    // Clamp target to our zone
    targetPos.x = BABYLON.Scalar.Clamp(targetPos.x, this._allowedZone.minX, this._allowedZone.maxX);
    targetPos.z = BABYLON.Scalar.Clamp(targetPos.z, this._allowedZone.minZ, this._allowedZone.maxZ);

    // Move toward target
    const dx = targetPos.x - myPos.x;
    const dz = targetPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist > 0.5) {
      const speed = dist > 5 ? 7.5 : 5.5;
      const vx = (dx / dist) * speed;
      const vz = (dz / dist) * speed;
      myPos.x += vx * dt;
      myPos.z += vz * dt;
      this._currentSpeed = speed;
      this._velocity.set(vx, 0, vz);

      const angle = Math.atan2(dx, dz);
      this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.15);
    } else {
      this._currentSpeed = 0;
    }

    // If close enough to ball, gain possession
    if (distToBall < 2.0) {
      this.hasBallPossession = true;
      this._state = AIState.ADVANCE;
    }
  }

  /**
   * Pass to striker behavior
   */
  private _handlePass(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3): void {
    if (this._kickTimer > 0) return;

    // Find striker position
    let passTarget: BABYLON.Vector3;
    
    if (this._strikerPosition) {
      passTarget = this._strikerPosition.clone();
    } else {
      // Default pass target - striker zone
      passTarget = new BABYLON.Vector3(0, 0, -15);
    }

    // Calculate pass direction and power
    const dx = passTarget.x - myPos.x;
    const dz = passTarget.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist > 0.1) {
      const passDir = new BABYLON.Vector3(dx / dist, 0.1, dz / dist).normalize();
      this._ball.kick(passDir, PASS_POWER, this._velocity);
      
      this._kickTimer = PASS_COOLDOWN;
      this._passTimer = PASS_COOLDOWN;
      this.animator.triggerKick(KickType.Normal);
      this.hasBallPossession = false;
      this._state = AIState.SUPPORT;
    }
  }

  /**
   * Advance with ball - dribble forward toward goal
   */
  private _handleAdvance(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, distToGoal: number, dt: number): void {
    // Direction toward goal
    const goalPos = new BABYLON.Vector3(0, 0, -30);
    const toGoal = goalPos.subtract(myPos).normalize();

    // Dribble toward goal
    if (this._kickTimer <= 0) {
      // Small touch toward goal
      const dribbleDir = new BABYLON.Vector3(toGoal.x * 0.5, 0.1, toGoal.z * 0.5).normalize();
      this._ball.kick(dribbleDir, 3.5, this._velocity);
      this._kickTimer = 0.35;
      this.animator.triggerKick(KickType.Normal);
    }

    // Move toward ball to maintain control
    const dx = ballPos.x - myPos.x;
    const dz = ballPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist > 0.3) {
      const vx = (dx / dist) * ADVANCE_SPEED;
      const vz = (dz / dist) * ADVANCE_SPEED;
      myPos.x += vx * dt;
      myPos.z += vz * dt;
      this._velocity.set(vx, 0, vz);
    } else {
      this._velocity.set(0, 0, 0);
    }

    this._currentSpeed = ADVANCE_SPEED;

    // Face toward goal
    const angle = Math.atan2(toGoal.x, toGoal.z);
    this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.15);

    // If in shooting range, transition to shoot
    if (distToGoal < 18 && Math.abs(myPos.x) < 5) {
      this._state = AIState.SHOOT;
    }
  }

  /**
   * Reset pass timer on position reset
   */
  resetPosition(): void {
    super.resetPosition();
    this._passTimer = 0;
  }
}